import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';

export interface TrafficFineDriverIndicationForm {
  name: string;
  cpf: string;
  rg: string;
  address: string;
  cep: string;
  city: string;
  state: string;
  cnhNumber: string;
  cnhRegister: string;
  cnhUf: string;
}

export interface DriverIndicationTemplateData {
  aipNumber: string;
  vehiclePlate: string;
  infractionDateTime: string;
  issuingAgency: string;
  infractionDescription: string;
  infractionLocation: string;
  indicationDeadline: string;
  indicationEndDate: string;
  todayDate: string;
  driverName: string;
  driverCpf: string;
  driverRg: string;
  driverAddress: string;
  driverCep: string;
  driverCity: string;
  driverState: string;
  cnhNumber: string;
  cnhRegister: string;
  cnhUf: string;
}

@Injectable({ providedIn: 'root' })
export class TrafficFineDriverIndicationDocumentService {
  private readonly templatePath = 'assets/documents/traffic-fines/formulario-indicacao-condutor.docx';

  constructor(private http: HttpClient) {}

  buildTemplateData(fine: any, form: TrafficFineDriverIndicationForm): DriverIndicationTemplateData {
    const indicationDeadline = this.resolveIndicationDeadline(fine);
    return {
      aipNumber: this.pickString(fine, ['aipNumber', 'AIPNumber']),
      vehiclePlate: this.pickString(fine, ['vehiclePlate', 'VehiclePlate', 'plateNumber', 'PlateNumber']),
      infractionDateTime: this.formatInfractionDateTime(fine),
      issuingAgency: this.resolveIssuingAgency(fine),
      infractionDescription: this.pickString(fine, [
        'description',
        'Description',
        'infractionDescription',
        'InfractionDescription'
      ]),
      infractionLocation: this.resolveInfractionLocation(fine),
      indicationDeadline,
      indicationEndDate: indicationDeadline,
      todayDate: this.formatTodayDate(),
      driverName: form.name?.trim() || '',
      driverCpf: form.cpf?.trim() || '',
      driverRg: form.rg?.trim() || '',
      driverAddress: form.address?.trim() || '',
      driverCep: form.cep?.trim() || '',
      driverCity: form.city?.trim() || '',
      driverState: form.state?.trim() || '',
      cnhNumber: form.cnhNumber?.trim() || '',
      cnhRegister: form.cnhRegister?.trim() || '',
      cnhUf: form.cnhUf?.trim() || ''
    };
  }

  async generateDocument(fine: any, form: TrafficFineDriverIndicationForm): Promise<Blob> {
    const arrayBuffer = await firstValueFrom(
      this.http.get(this.templatePath, { responseType: 'arraybuffer' })
    );

    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      throw new Error('Modelo do formulário de indicação não encontrado.');
    }

    try {
      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        nullGetter: () => ''
      });
      doc.render(this.buildTemplateData(fine, form) as unknown as Record<string, unknown>);
      const output = doc.getZip().generate({
        type: 'arraybuffer',
        compression: 'DEFLATE'
      });
      return new Blob([output], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
    } catch (error: any) {
      const message =
        error?.properties?.errors?.map((e: any) => e.message).join('; ') ||
        error?.message ||
        'Erro ao preencher o documento.';
      throw new Error(message);
    }
  }

  async downloadDocument(fine: any, form: TrafficFineDriverIndicationForm): Promise<void> {
    const blob = await this.generateDocument(fine, form);
    const aip = this.pickString(fine, ['aipNumber', 'AIPNumber']) || 'multa';
    const safeAip = aip.replace(/[^\w.-]+/g, '_');
    saveAs(blob, `indicacao-condutor-${safeAip}.docx`);
  }

  private resolveIndicationDeadline(fine: any): string {
    return (
      this.formatDate(
        this.pickRaw(fine, [
          'indicationDeadline',
          'IndicationDeadline',
          'driverIndicationDeadline',
          'DriverIndicationDeadline',
          'dueDate',
          'DueDate'
        ])
      ) || ''
    );
  }

  private resolveIssuingAgency(fine: any): string {
    const agency = this.pickString(fine, [
      'issuingAgency',
      'IssuingAgency',
      'agencyName',
      'AgencyName',
      'orgaoAutuador',
      'OrgaoAutuador',
      'trafficAgency',
      'TrafficAgency'
    ]);
    if (agency) {
      return agency;
    }

    const city = this.pickString(fine, ['city', 'City', 'infractionCity', 'InfractionCity']);
    const state = this.pickString(fine, ['state', 'State', 'uf', 'UF']);
    if (city && state) {
      return `PREFEITURA MUNICIPAL DE ${city.toUpperCase()} - ${state.toUpperCase()}`;
    }
    if (city) {
      return `PREFEITURA MUNICIPAL DE ${city.toUpperCase()}`;
    }
    return '';
  }

  private resolveInfractionLocation(fine: any): string {
    const location = this.pickString(fine, [
      'infractionLocation',
      'InfractionLocation',
      'location',
      'Location',
      'address',
      'Address'
    ]);
    if (location) {
      return location;
    }

    const city = this.pickString(fine, ['city', 'City', 'infractionCity', 'InfractionCity']);
    const state = this.pickString(fine, ['state', 'State', 'uf', 'UF']);
    if (city && state) {
      return `${city} - ${state}`;
    }
    return city || state || '';
  }

  private formatInfractionDateTime(fine: any): string {
    const raw = this.pickRaw(fine, [
      'infractionDateTime',
      'InfractionDateTime',
      'issueDate',
      'IssueDate',
      'infractionDate',
      'InfractionDate'
    ]);
    if (!raw) {
      return '';
    }

    const date = this.parseDate(raw);
    if (!date) {
      return String(raw);
    }

    const datePart = date.toLocaleDateString('pt-BR');
    const hasTime =
      typeof raw === 'string' &&
      (raw.includes('T') || /\d{2}:\d{2}/.test(raw) || raw.length > 10);
    if (!hasTime) {
      return datePart;
    }

    const timePart = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    return `${datePart} - ${timePart}`;
  }

  private formatTodayDate(): string {
    return new Date().toLocaleDateString('pt-BR');
  }

  private formatDate(value: any): string {
    const date = this.parseDate(value);
    return date ? date.toLocaleDateString('pt-BR') : '';
  }

  private parseDate(value: any): Date | null {
    if (value == null || value === '') {
      return null;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private pickString(obj: any, keys: string[]): string {
    const value = this.pickRaw(obj, keys);
    return value != null && String(value).trim() !== '' ? String(value).trim() : '';
  }

  private pickRaw(obj: any, keys: string[]): any {
    if (!obj) {
      return null;
    }
    for (const key of keys) {
      const value = obj[key];
      if (value != null && value !== '') {
        return value;
      }
    }
    return null;
  }
}
